import { OrderSource } from '@afterpaytouch/portal-api/types'
import { OfferDetail } from '@afterpaytouch/portal-api/consumer/checkout/types'
import { DataVault, EnrichedItem } from '../../model'

export const isAppleRetailOrder = (orderSource: OrderSource | unknown): orderSource is OrderSource => {
  switch (orderSource) {
    case OrderSource.APPLE_RETAIL_IN_STORE:
    case OrderSource.APPLE_RETAIL_ONLINE:
    case OrderSource.APPLE_RETAIL_TELESALES:
    case OrderSource.APPLE_RETAIL_APPLE_STORE_APP:
      return true
    default:
      return false
  }
}

const KNOWN_ORDER_SOURCES: ReadonlySet<string> = new Set(Object.values(OrderSource))

/**
 * APL-1424: a verified non-Apple Retail order source, checked on the raw value and therefore
 * independent of the APPLE_RETAIL_ENABLED flag that gates isAppleRetailOrder — a flag-off
 * Apple Retail order must never reach the Pay Monthly terminal. NO_VALUE, missing and
 * unrecognized sources are rejected: an unconfigured source cannot prove a non-Apple order,
 * so routing fails safe to existing behavior.
 */
export const isKnownNonAppleRetailOrderSource = (orderSource: OrderSource | undefined): boolean =>
  orderSource !== undefined && orderSource !== OrderSource.NO_VALUE && KNOWN_ORDER_SOURCES.has(orderSource) && !isAppleRetailOrder(orderSource)

type ChildRule<Entity extends { id: string }, ChildProp extends string, ChildValue> = {
  childPropertyName: ChildProp
  getParentId: (item: Entity) => string | null | undefined
  getChildValue: (child: Entity) => ChildValue
}

export const buildDataVault = <Entity extends { id: string }, ChildProp extends string = 'childItem', ChildValue = Entity>(
  items: Entity[],
  rule?: ChildRule<Entity, ChildProp, ChildValue>
): DataVault<Entity & Partial<Record<ChildProp, ChildValue>>> => {
  type VaultEntity = Entity & Partial<Record<ChildProp, ChildValue>>

  const data = items.reduce<Record<string, VaultEntity>>((acc, item) => {
    acc[item.id] = item as VaultEntity
    return acc
  }, {} as Record<string, VaultEntity>)

  if (rule === undefined) {
    return {
      ids: items.map((item) => item.id),
      data,
    }
  }

  const allIds = new Set<string>(items.map((i) => i.id))

  const ids = items
    .filter((item) => {
      const parentId = rule.getParentId(item)
      return !(typeof parentId === 'string' && parentId.length > 0 && allIds.has(parentId))
    })
    .map((item) => item.id)

  for (const item of items) {
    const parentId = rule.getParentId(item)
    if (typeof parentId === 'string' && parentId.length > 0 && parentId in data) {
      const parent = data[parentId]
      const prop = rule.childPropertyName
      if (parent[prop] === undefined) {
        ;(parent as unknown as Record<ChildProp, ChildValue | undefined>)[prop] = rule.getChildValue(item)
      }
    }
  }

  return { ids, data }
}

export const mergeOffersIntoItemsVault = (vault: DataVault<EnrichedItem>, offers: OfferDetail[] | undefined): DataVault<EnrichedItem> => {
  if (!Array.isArray(offers) || offers.length === 0) {
    return vault
  }

  for (const offer of offers) {
    if (offer.rejectedReasonCode !== undefined && offer.rejectedReasonCode !== null) {
      continue
    }

    const id = offer.id
    if (typeof id !== 'string' || id.length === 0) {
      continue
    }

    const item = vault.data[id]
    if (item === undefined) {
      continue
    }

    item.apr = offer.apr
    item.numberOfInstallments = offer.numberOfInstallments
    item.paymentSchedules = offer.paymentSchedules
    item.monthlyAmount = offer.monthlyAmount
    item.amountDueToday = offer.amountDueToday
    item.paymentType = offer.paymentType
    item.downPaymentAmount = offer.downPaymentAmount
    item.interestAmount = offer.interestAmount
    item.totalFinancedAmount = offer.totalFinancedAmount
    item.items = offer.items
  }

  return vault
}
